import {
  createContext,
  useContext,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';

import { useLocation } from 'react-router-dom';

import { useMergedRefs } from '@/mastodon/hooks/useMergedRefs';
import type { PolymorphicProps } from '@/types/polymorphic';

import type { MastodonLocation } from '../router';

export const FOCUS_TARGET = {
  POST: 'detailed-status',
  SEARCH: 'search',
} as const;

export type NamedFocusTarget = (typeof FOCUS_TARGET)[keyof typeof FOCUS_TARGET];

export type FocusTarget = boolean | NamedFocusTarget;

const FocusTargetContext = createContext<React.RefObject<FocusTarget> | null>(
  null,
);

/**
 * `FocusTargetProvider` keeps track of whether focus should be
 * set after a navigation. By default, any navigation will set the
 * current value of `focusTargetRef` to `true`, which will cause
 * the `NavigationFocusTarget` component to focus itself when it mounts.
 *
 * To disable this behaviour for a navigation, the focus target can be
 * set to `false` using location state, for example:
 * ```
 * location.push(url, { focusTarget: false });
 * ```
 *
 * If the target page contains multiple `NavigationFocusTarget` components
 * (e.g. a main heading and a post that should be focused), give the more
 * specific `NavigationFocusTarget` instance a name, and pass the same name
 * via location state:
 * ```
 * location.push(url, { focusTarget: 'detailed-status' });
 * ```
 */

export const FocusTargetProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const focusTargetRef = useRef<FocusTarget>(false);
  const previousLocationRef = useRef<
    | (Pick<MastodonLocation, 'pathname' | 'search'> & {
        focusTarget?: FocusTarget;
      })
    | null
  >(null);

  const { pathname, search, state } = useLocation<{
    focusTarget?: FocusTarget;
  } | null>();

  const { focusTarget } = state ?? {};

  useLayoutEffect(() => {
    // We never want to set focus on page load, so we keep
    // track of whether a manual navigation has occurred by comparing
    // our current with the previous location:
    const previous = previousLocationRef.current;

    // Bail out on the first render, populate previousLocationRef
    if (previous === null) {
      previousLocationRef.current = { pathname, search, focusTarget };
      return;
    }

    // Bail out if location hasn't changed
    if (
      previous.pathname === pathname &&
      previous.search === search &&
      previous.focusTarget === focusTarget
    ) {
      return;
    }

    // Location has changed:
    // - Set focusTarget
    // – Store current location as previous
    // (We store `focusTarget` as `false` to allow overriding it.)
    previousLocationRef.current = { pathname, search, focusTarget: false };
    focusTargetRef.current = focusTarget ?? true;
  }, [pathname, search, focusTarget]);

  return (
    <FocusTargetContext.Provider value={focusTargetRef}>
      {children}
    </FocusTargetContext.Provider>
  );
};

export function useFocusAfterNavigation(
  targetName?: NamedFocusTarget,
  onAfterFocus?: (hasSetFocus: boolean) => void,
) {
  const focusTargetRef = useContext(FocusTargetContext);
  const hasFocusedOnceRef = useRef(false);

  return useCallback(
    (element: HTMLElement | null) => {
      const focusTarget = focusTargetRef?.current;

      // Bail out if focusTarget was set to `false`
      if (!element || !focusTarget || hasFocusedOnceRef.current) {
        onAfterFocus?.(false);
        return;
      }

      const shouldSetFocus = targetName
        ? focusTarget === targetName
        : focusTarget === true;

      if (shouldSetFocus) {
        setTimeout(() => {
          element.focus({ preventScroll: true });
          onAfterFocus?.(true);
          // Prevent this focus handler from setting focus again
          hasFocusedOnceRef.current = true;
        }, 0);
      } else {
        onAfterFocus?.(false);
      }
    },
    [focusTargetRef, onAfterFocus, targetName],
  );
}

interface FocusTargetElementProps extends React.ComponentPropsWithoutRef<'h1'> {
  focusTargetName?: NamedFocusTarget;
}

export const NavigationFocusTarget = <As extends React.ElementType = 'h1'>({
  as: asComp,
  focusTargetName,
  children,
  ...otherProps
}: PolymorphicProps<FocusTargetElementProps, As>) => {
  const Component = asComp ?? 'h1';
  const focusAfterNavigation = useFocusAfterNavigation(focusTargetName);

  return (
    <Component
      {...otherProps}
      ref={useMergedRefs(focusAfterNavigation, otherProps.ref)}
      tabIndex={-1}
    >
      {children}
    </Component>
  );
};
